import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserCheck } from 'lucide-react';
import AppIcon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { useMinWidth } from '../../../hooks/media-query';
import TeamMemberCard from './components/TeamMemberCard';
import ReflectionDetailPanel from './components/ReflectionDetailPanel';
import FilterPanel from './components/FilterPanel';
import TeamMetrics from './components/TeamMetrics';
import { getTeamReflections, getReflectionPrompts, submitManagerResponse, getManagerResponses } from '../../../api/PreformanceManagement/Reflection';
import { pickReflectionCategory, pickReflectionTitle } from '../Reflections/reflectionPromptText';

interface Reflection {
  id: number;
  promptId: number;
  promptTitle: string;
  category: string;
  icon: string;
  response: string;
}

interface ManagerResponseData {
  message: string;
  respondedAt: string;
}

interface TeamMember {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeAvatar?: string;
  position: string;
  department: string;
  reflectionMonth: string;
  submittedDate?: string;
  status: string;
  completionRate: number;
  totalPrompts: number;
  completedPrompts: number;
  hasResponse: boolean;
  responseDate?: string;
  reflections?: Reflection[];
  managerResponse?: ManagerResponseData;
}

interface Filters {
  dateRange: string;
  status: string;
  searchQuery: string;
}

interface Metrics {
  totalTeamMembers: number;
  submitted: number;
  pending: number;
  reviewed: number;
  participationRate: number;
  responseRate: number;
}

interface ManagerReflectionResponsesProps {
  onViewDetails?: (reflectionId: string) => void;
  onRespond?: (reflectionId: string, response: string) => Promise<void>;
}

const ManagerReflectionResponses: React.FC<ManagerReflectionResponsesProps> = ({
  onViewDetails,
  onRespond
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast, showToast, dismissToast } = useToast();
  const isDesktop = useMinWidth(768);

  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [filters, setFilters] = useState<Filters>({
    dateRange: 'all',
    status: 'all',
    searchQuery: ''
  });

  const loadReflectionData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [reflectionsResponse, promptsResponse] = await Promise.all([
        getTeamReflections(),
        getReflectionPrompts()
      ]);

      if (Array.isArray(reflectionsResponse) && Array.isArray(promptsResponse)) {
        const transformedMembers = await transformApiDataToTeamMembers(reflectionsResponse, promptsResponse);
        setTeamMembers(transformedMembers);

        if (selectedMember) {
          const updatedMember = transformedMembers.find(m => m.id === selectedMember.id);
          if (updatedMember) setSelectedMember(updatedMember);
        }
      } else {
        setTeamMembers([]);
      }
    } catch {
      setError(t('performance.managerResponse.failedToLoad'));
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  // Prompt titles are picked per language while transforming, so a language switch reloads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const transformApiDataToTeamMembers = async (reflections: any[], prompts: any[]): Promise<TeamMember[]> => {
    const userMap = new Map<string, TeamMember>();

    reflections.forEach((reflection) => {
      const userId = reflection.userId;

      if (!userMap.has(userId)) {
        const completedCount = reflections.filter(r => r.userId === userId).length;
        const imageUrl = reflection.userImageUrl || reflection.imageUrl;

        userMap.set(userId, {
          id: userId,
          employeeId: userId,
          employeeName: reflection.userName || t('performance.managerResponse.unknownUser'),
          employeeAvatar: imageUrl || undefined,
          position: reflection.position || t('performance.managerResponse.employee'),
          department: reflection.department || t('performance.managerResponse.unknown'),
          reflectionMonth: reflection.periodName || t('performance.managerResponse.currentPeriod'),
          submittedDate: reflection.submittedAt,
          status: 'pending',
          completionRate: Math.round((completedCount / prompts.length) * 100),
          totalPrompts: prompts.length,
          completedPrompts: completedCount,
          hasResponse: false,
          responseDate: undefined,
          reflections: [],
          managerResponse: undefined
        });
      }

      const userData = userMap.get(userId)!;
      // Title and category come from the SERVER (UserReflectionView.PromptTitle, resolved
      // status-blind through IReflectionPromptResolver). Looking them up in the ACTIVE prompt
      // list — the old behaviour — blanked every reflection whose prompt HR had since switched
      // off. The active list is still consulted for the icon, which the reflection view lacks.
      const prompt = prompts.find(p => p.id === reflection.promptId);

      userData.reflections!.push({
        id: reflection.id,
        promptId: reflection.promptId,
        promptTitle: pickReflectionTitle(reflection, i18n.language) || `${t('performance.managerResponse.reflectionPrompt')} #${reflection.promptId}`,
        category: pickReflectionCategory(reflection, i18n.language) || t('performance.managerResponse.reflection'),
        icon: prompt?.iconName || 'MessageSquare',
        response: reflection.responseText || reflection.reflectionText || ''
      });
    });

    const userIds = Array.from(userMap.keys());
    await Promise.all(
      userIds.map(async (userId) => {
        try {
          const managerResponsesData = await getManagerResponses(userId);
          const userData = userMap.get(userId)!;

          if (managerResponsesData && managerResponsesData.length > 0) {
            const latestResponse = managerResponsesData[0];
            userData.hasResponse = true;
            userData.status = 'reviewed';
            userData.responseDate = latestResponse.respondedAt;
            userData.managerResponse = {
              message: latestResponse.message,
              respondedAt: latestResponse.respondedAt
            };
          }
        } catch {
          // Silently fail for individual user response fetch
        }
      })
    );

    const membersArray = Array.from(userMap.values());
    membersArray.sort((a, b) => {
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      if (a.submittedDate && b.submittedDate) {
        return new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime();
      }
      return a.employeeName.localeCompare(b.employeeName);
    });

    return membersArray;
  };

  useEffect(() => {
    if (isDesktop && teamMembers.length > 0 && !selectedMember) {
      setSelectedMember(teamMembers[0]);
    }
  }, [isDesktop, teamMembers, selectedMember]);

  useEffect(() => {
    loadReflectionData();
  }, [loadReflectionData]);

  const handleFilterChange = (filterType: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ dateRange: 'all', status: 'all', searchQuery: '' });
  };

  const handleMemberSelect = (member: TeamMember) => {
    setSelectedMember(member);
  };

  const handleResponseSubmit = async (employeeId: string, response: string) => {
    try {
      setSubmitting(true);

      const member = teamMembers.find(m => m.id === employeeId);
      if (!member || !member.reflections || member.reflections.length === 0) {
        throw new Error(t('performance.managerResponse.noReflectionsFound'));
      }

      const reflectionId = member.reflections[0].id;

      if (onRespond) {
        await onRespond(reflectionId.toString(), response);
      } else {
        await submitManagerResponse({
          userReflectionId: reflectionId,
          message: response
        });
      }

      showToast(t('performance.managerResponse.responseSubmitted'), 'success');
      await loadReflectionData();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0]?.description ||
                          t('performance.managerResponse.failedToSubmitResponse');
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter team reflections
  const filteredReflections = teamMembers.filter(member => {
    const matchesSearch = member.employeeName.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
                         member.department.toLowerCase().includes(filters.searchQuery.toLowerCase());

    const matchesStatus = filters.status === 'all' || member.status === filters.status;

    let matchesDate = true;
    if (filters.dateRange !== 'all' && member.submittedDate) {
      const submittedDate = new Date(member.submittedDate);
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const submittedMonth = submittedDate.getMonth();
      const submittedYear = submittedDate.getFullYear();

      if (filters.dateRange === 'current') {
        matchesDate = submittedMonth === currentMonth && submittedYear === currentYear;
      } else if (filters.dateRange === 'last-month') {
        const lastMonth = new Date(currentYear, currentMonth - 1, 1);
        matchesDate = submittedMonth === lastMonth.getMonth() && submittedYear === lastMonth.getFullYear();
      } else if (filters.dateRange === 'last-3-months') {
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        matchesDate = submittedDate >= threeMonthsAgo;
      } else if (filters.dateRange === 'last-6-months') {
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        matchesDate = submittedDate >= sixMonthsAgo;
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const calculateMetrics = (): Metrics => {
    const total = teamMembers.length;
    const submitted = teamMembers.filter(m => m.status !== 'not_submitted').length;
    const pending = teamMembers.filter(m => m.status === 'pending').length;
    const reviewed = teamMembers.filter(m => m.status === 'reviewed').length;
    const participationRate = total > 0 ? Math.round((submitted / total) * 100) : 0;
    const responseRate = submitted > 0 ? Math.round((reviewed / submitted) * 100) : 0;
    return { totalTeamMembers: total, submitted, pending, reviewed, participationRate, responseRate };
  };

  const metrics = calculateMetrics();

  if (loading && teamMembers.length === 0) {
    return (
      <PageLayout
        title={t('performance.managerResponse.teamReflections')}
        description={t('performance.managerResponse.reviewAndRespond')}
        icon={UserCheck}
        accentColor="rose"
        maxWidth="1600px"
      >
        <div className="flex justify-center items-center min-h-[200px] gap-3">
          <Spinner />
          <span className="text-sm text-muted-foreground">{t('performance.managerResponse.loadingReflections')}</span>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout
        title={t('performance.managerResponse.teamReflections')}
        description={t('performance.managerResponse.reviewAndRespond')}
        icon={UserCheck}
        accentColor="rose"
        maxWidth="1600px"
      >
        <div className="bg-destructive/10 p-6 rounded-xl text-center">
          <AppIcon name="AlertCircle" size={48} className="mx-auto text-destructive mb-3" />
          <p className="text-destructive mb-3">{error}</p>
          <Button variant="outline" onClick={loadReflectionData}>
            {t('common.retry')}
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={t('performance.managerResponse.teamReflections')}
      description={t('performance.managerResponse.reviewAndRespond')}
      icon={UserCheck}
      accentColor="rose"
      maxWidth="1600px"
      headerActions={
        <Button
          variant="outline"
          onClick={loadReflectionData}
          disabled={loading}
        >
          {loading ? <Spinner size="sm" className="me-2" /> : <AppIcon name="RefreshCw" size={16} className="me-2" />}
          {t('common.refresh')}
        </Button>
      }
    >

        {/* Main Content */}
        <div className="py-2 flex justify-center">
          <div className="w-full md:w-4/5 max-w-[1400px] mx-auto flex flex-col gap-4">
            <TeamMetrics metrics={metrics} />

            {/* Mobile Layout */}
            <div className="md:hidden flex flex-col gap-4">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
              {selectedMember ? (
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedMember(null)}
                    className="mb-3"
                  >
                    <AppIcon name="ArrowLeft" size={16} className="me-2" />
                    {t('performance.managerResponse.backToTeamList')}
                  </Button>
                  <ReflectionDetailPanel
                    member={selectedMember}
                    onResponseSubmit={handleResponseSubmit}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {filteredReflections.map((member) => (
                    <TeamMemberCard
                      key={member.id}
                      member={member}
                      onClick={() => handleMemberSelect(member)}
                      isSelected={false}
                    />
                  ))}
                  {filteredReflections.length === 0 && (
                    <div className="text-center py-8 bg-card border border-border rounded-lg">
                      <AppIcon name="Search" size={48} className="mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">{t('performance.managerResponse.noMembersMatch')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:grid grid-cols-[1fr_2fr] gap-4 h-[calc(100vh-200px)]">
              <div className="flex flex-col gap-3 h-full">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                />
                <div className="flex flex-col gap-2 overflow-y-auto flex-1">
                  {filteredReflections.map((member) => (
                    <TeamMemberCard
                      key={member.id}
                      member={member}
                      onClick={() => handleMemberSelect(member)}
                      isSelected={selectedMember?.id === member.id}
                    />
                  ))}
                  {filteredReflections.length === 0 && (
                    <div className="text-center py-8">
                      <AppIcon name="Search" size={32} className="mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">{t('performance.managerResponse.noMatchesFound')}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="h-full">
                <ReflectionDetailPanel
                  member={selectedMember || undefined}
                  onResponseSubmit={handleResponseSubmit}
                />
              </div>
            </div>
          </div>
        </div>
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default ManagerReflectionResponses;
